import { skipToken } from '@reduxjs/toolkit/query';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { isValidUrl } from 'components/forms/inputs/rhf-validators/validators';
import AddContactModal from 'components/modals/contact/AddContactModal';
import { useModal } from 'components/modals/useModal';
import { FORM_OVERFLOW_CUSTOM_STYLE } from 'components/modals/util';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useGetActiveBankAccountsQuery, useGetCompaniesListQuery, useGetCompanyByIdQuery } from 'store/api/companies';
import { useGetContractTemplatesWithFiltersQuery } from 'store/api/contracts';
import { useGetTenantConfigurationQuery } from 'store/api/tenants.ts';
import { ClassifierData } from 'types/classifiers';
import { ContactDto, PartyType } from 'types/contacts';
import { ContractForm, ContractType } from 'types/contracts';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { UpdateHourlyRatesDto } from 'types/matters';
import { PricingKind, StagedPricelistPeriod } from 'types/pricelists';
import { NamedEntityMinDto } from 'types/other';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { buildTimecardIncrementOptions, toTimecardIncrementOption } from 'utils/timecardIncrementOptions';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';

export type ContractFormProps = {
    type: 'add' | 'edit';
    initialData?: ContractForm;
    classifiers: Record<string, ClassifierData>;
    refetch?: () => unknown;
    entityCustomFields?: CustomField[];
    isDeletable?: boolean;
};

const defaultValues: ContractForm = {
    billingNotes: '',
    contractName: '',
    nameExtension: '',
    contractType: null,
    documentTemplateType: null,
    isClientTemplate: false,
    contractTemplate: null,
    tenantCompany: null,
    signedByPersonParty: null,
    companies: [],
    parties: [],
    clientReference: '',
    contractNumber: '',
    dmsLink: '',
    dmsNumber: '',
    contractSentAt: null,
    contractSignedAt: null,
    contractTerminatedAt: null,
    financialConditionId: null,
    paymentDeadlineInDays: null,
    prepaymentPaymentDeadlineInDays: null,
    invoicingInfo: '',
    invoicingIntervalType: null,
    discountPct: null,
    interestRatePct: null,
    interestRateB2cPct: null,
    currencyType: null,
    timecardSpecificationLayoutType: null,
    timecardIncrement: null,
    pricelistId: null,
    pricelist: null,
    pricingHash: undefined,
    pricelistRates: [],
    readOnlyCreatedAt: null,
    beneficialOwners: [],
    beneficialOwnerNotes: '',
    contractRelatedKeywords: [],
    adminFeePercentage: null,
    additionalNotes: '',
    bankAccount: null,
};

/**
 * The contract form's state, rules and handlers, shared by the whole form and by each section edited on its own:
 * the fields in `form-sections/ContractFields` read it.
 */
export const useContractForm = ({ type = 'add', initialData, classifiers, entityCustomFields }: ContractFormProps) => {
    const { openModal, closeModal } = useModal();

    const methods = useForm<ContractForm>({
        defaultValues: initialData ? { ...defaultValues, ...initialData } : defaultValues,
        values: initialData,
    });

    const { control, setValue, getValues } = methods;
    const {
        customFields,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({
        entityType: EntityType.CONTRACT,
        fields: entityCustomFields,
    });

    const validUrl = useCallback((val: unknown) => isValidUrl(val, t('validation.dms-link-must-be-valid')), []);

    const [
        parties,
        tenantCompany,
        contractType,
        pricelist,
        currencyType,
        contractTemplate,
        contractRelatedKeywords,
        timecardIncrement,
    ] = useWatch({
        control,
        name: [
            'parties',
            'tenantCompany',
            'contractType',
            'pricelist',
            'currencyType',
            'contractTemplate',
            'contractRelatedKeywords',
            'timecardIncrement',
        ],
    });

    const isServiceAgreement = contractType?.value === ContractType.SERVICE_AGREEMENT;

    const hasAdminFeePermitted =
        isServiceAgreement &&
        contractRelatedKeywords?.some((kw: { value: string }) => kw.value === 'ADMIN_FEE_PERMITTED');

    useEffect(() => {
        if (!hasAdminFeePermitted) {
            setValue('adminFeePercentage', null);
        }
    }, [hasAdminFeePermitted, setValue]);

    useEffect(() => {
        if (contractType?.value && contractType.value !== ContractType.SERVICE_AGREEMENT) {
            setValue('nameExtension', '');
        }
    }, [contractType?.value, setValue]);

    // Filter custom fields based on contract type
    const filteredCustomFields = useMemo(() => {
        // Njord LV specific fields that only apply to SERVICE_AGREEMENT contracts
        const serviceAgreementOnlyFields = [
            'additionalFinancialTermsCurrency',
            'additionalFinancialTermsAmount',
            'additionalFinancialTermsNotes',
        ];

        return customFields.filter((cf) => {
            // If it's a SERVICE_AGREEMENT-only field, only show for SERVICE_AGREEMENT
            if (serviceAgreementOnlyFields.includes(cf.apiFieldName)) {
                return contractType?.value === ContractType.SERVICE_AGREEMENT;
            }

            // All other custom fields show for all contract types
            return true;
        });
    }, [customFields, contractType?.value]);
    const firstPartyWithTypeCompany = parties?.find((party) => party.partyType === PartyType.COMPANY);

    const selectedPersonPartyIds =
        parties?.filter((party) => party.partyType === PartyType.PERSON).map((party) => party.id) || [];
    const selectedCompanyPartyIds =
        parties?.filter((party) => party.partyType === PartyType.COMPANY).map((party) => party.id) || [];

    const { data: companyList, isLoading: isLoadingCompanies } = useGetCompaniesListQuery();
    const activeCompanyList = useMemo(() => companyList?.filter((c) => c.isActive !== false) ?? [], [companyList]);
    const { data: bankAccountsGrouped = [] } = useGetActiveBankAccountsQuery(undefined);
    const isDownloadContractTemplateEnabled = useIsFeatureEnabled(FeatureFlag.DOWNLOAD_CONTRACT_TEMPLATE);
    const isTimecardIncrementControlEnabled = useIsFeatureEnabled(FeatureFlag.TIMECARD_INCREMENT_CONTROL);
    // The configuration read is refused without a back-office right, and a refusal toasts.
    const canReadConfiguration = useHasPermissions(['READ.BACK_OFFICE_SETTINGS', 'GET.BACK_OFFICE_SETTINGS_WO_READ']);
    const isAdminFeeDefaultEnabled = useIsFeatureEnabled(FeatureFlag.ADMIN_FEE_MODAL_INVOICES) && canReadConfiguration;
    const { data: tenantConfiguration } = useGetTenantConfigurationQuery(undefined, {
        skip: !isTimecardIncrementControlEnabled && !isAdminFeeDefaultEnabled,
    });
    const defaultAdminFeePercentage = isAdminFeeDefaultEnabled ? tenantConfiguration?.defaultAdminFeePercentage : null;

    // Only a keyword picked in this form takes the default, possibly once the configuration arrives: a contract
    // opened with the keyword keeps the admin fee it was stored with, even an empty one.
    const hadAdminFeePermitted = useRef(hasAdminFeePermitted);
    const awaitsDefaultAdminFee = useRef(false);
    useEffect(() => {
        if (hasAdminFeePermitted !== hadAdminFeePermitted.current) {
            hadAdminFeePermitted.current = hasAdminFeePermitted;
            awaitsDefaultAdminFee.current = hasAdminFeePermitted;
        }
        if (awaitsDefaultAdminFee.current && defaultAdminFeePercentage != null) {
            awaitsDefaultAdminFee.current = false;
            if (!getValues('adminFeePercentage')) {
                setValue('adminFeePercentage', String(defaultAdminFeePercentage));
            }
        }
    }, [defaultAdminFeePercentage, getValues, hasAdminFeePermitted, setValue]);
    const { data: contractTemplatesOptions, isLoading: isLoadingContractTemplatesOptions } =
        useGetContractTemplatesWithFiltersQuery(
            {
                filters: `contractTypeCode==${contractType?.value}`,
            },
            {
                skip: !contractType || !isDownloadContractTemplateEnabled,
            }
        );

    // Fetch company info on tenantCompany (concluded by) change to get financialy conditions info
    const { data: concludedByCompanyData } = useGetCompanyByIdQuery(tenantCompany?.id ?? skipToken);

    useEffect(() => {
        if (
            !contractType ||
            (contractTemplate?.contractTypeCode !== undefined &&
                contractType.value !== contractTemplate.contractTypeCode)
        ) {
            setValue('contractTemplate', null);
        }
    }, [contractTemplate?.contractTypeCode, contractType, setValue]);

    // A newly chosen 'concluded by' company prefills its financial conditions; the currency only while none is set.
    // TODO: cascading-set-state skip — RHF `setValue` prefills for multiple independent financial fields from async company data.
    useEffect(() => {
        if (!concludedByCompanyData) return;
        if (concludedByCompanyData?.id == initialData?.tenantCompany?.id) return;

        if (!currencyType?.value && !pricelist?.currencyTypeCode) {
            const updatedCurrencyType =
                (
                    getValidOptionsFromClassifier(classifiers['CURRENCY']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === concludedByCompanyData?.financialCondition?.currencyTypeCode) ?? null;
            setValue('currencyType', updatedCurrencyType);
        }

        // Just in case check if valid number
        const updatedPaymentDeadlineInDays =
            concludedByCompanyData?.financialCondition?.paymentDeadlineInDays &&
            !isNaN(Number(concludedByCompanyData?.financialCondition?.paymentDeadlineInDays))
                ? Number(concludedByCompanyData.financialCondition.paymentDeadlineInDays)
                : null;
        setValue('paymentDeadlineInDays', updatedPaymentDeadlineInDays);

        const updatedPrepaymentPaymentDeadlineInDays =
            concludedByCompanyData?.financialCondition?.prepaymentPaymentDeadlineInDays &&
            !isNaN(Number(concludedByCompanyData?.financialCondition?.prepaymentPaymentDeadlineInDays))
                ? Number(concludedByCompanyData.financialCondition.prepaymentPaymentDeadlineInDays)
                : null;
        setValue('prepaymentPaymentDeadlineInDays', updatedPrepaymentPaymentDeadlineInDays);

        const updatedInvoicingIntervalType =
            (
                getValidOptionsFromClassifier(classifiers['INVOICING_INTERVAL_TYPE']) as {
                    label: string;
                    value: string;
                }[]
            ).find((val) => val.value === concludedByCompanyData?.financialCondition?.invoicingIntervalTypeCode) ??
            null;
        setValue('invoicingIntervalType', updatedInvoicingIntervalType);

        // Just in case check if valid number despite being displayed as a string
        const updatedInterestRatePct = !isNaN(Number(concludedByCompanyData?.financialCondition?.interestRatePct))
            ? String(concludedByCompanyData?.financialCondition?.interestRatePct)
            : '';
        setValue('interestRatePct', updatedInterestRatePct);

        const updatedInterestRateB2cPct = !isNaN(Number(concludedByCompanyData?.financialCondition?.interestRateB2cPct))
            ? String(concludedByCompanyData?.financialCondition?.interestRateB2cPct)
            : '';
        setValue('interestRateB2cPct', updatedInterestRateB2cPct);
    }, [
        classifiers,
        concludedByCompanyData,
        currencyType?.value,
        initialData?.tenantCompany?.id,
        pricelist?.currencyTypeCode,
        setValue,
    ]);

    useEffect(() => {
        if (pricelist?.currencyTypeCode && currencyType?.value && pricelist.currencyTypeCode !== currencyType.value) {
            setValue('pricelist', null);
            setValue('pricelistRates', []);
        }
    }, [currencyType?.value, pricelist?.currencyTypeCode, setValue]);

    const defaultTimecardIncrement = tenantConfiguration?.defaultTimecardIncrement ?? null;

    // Prefill the tenant default increment whenever a service agreement has no increment yet,
    // covering new contracts, type switches to service agreement and legacy contracts saved
    // before the increment existed. A value that is already set is left untouched.
    useEffect(() => {
        if (!isTimecardIncrementControlEnabled || !isServiceAgreement || defaultTimecardIncrement == null) {
            return;
        }
        if (getValues('timecardIncrement') != null) {
            return;
        }
        setValue('timecardIncrement', toTimecardIncrementOption(defaultTimecardIncrement));
    }, [isTimecardIncrementControlEnabled, isServiceAgreement, defaultTimecardIncrement, getValues, setValue]);

    const timecardIncrementOptions = useMemo(
        () => buildTimecardIncrementOptions(tenantConfiguration?.timecardIncrementValues, timecardIncrement?.value),
        [tenantConfiguration?.timecardIncrementValues, timecardIncrement?.value]
    );

    const onAddNewContactPerson = (newContact: ContactDto) => {
        setValue('signedByPersonParty', { id: newContact.id, name: newContact.name });
    };

    const openAddNewContactModal = (): void => {
        openModal(
            <AddContactModal
                onContactAdd={onAddNewContactPerson}
                onClose={closeModal}
                contactType={PartyType.PERSON}
                preSelectedEmployer={
                    firstPartyWithTypeCompany
                        ? {
                              id: firstPartyWithTypeCompany.id,
                              name: firstPartyWithTypeCompany.name,
                              partyType: PartyType.COMPANY,
                          }
                        : undefined
                }
                connectedContactsGroupName={t('contacts.groups.contacts-connected-to-contract')}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    };

    const onNewPricelistFormSubmit = (
        pricelistData: UpdateHourlyRatesDto,
        closeModal: () => void,
        currencyTypeCode?: string
    ): void => {
        setValue('pricingHash', pricelistData.referencedPricelist.pricingHash);
        setValue('pricelistRates', pricelistData.rates);
        setValue('pricelistPeriods', null);
        setValue('pricelistFollows', null);
        setValue('pricelist', {
            name: '',
            scopeTypeCode: 'CONTRACT',
            currencyTypeCode: currencyTypeCode ?? null,
        });
        closeModal();
    };

    const onNewEmployeePricelistSubmit = (
        periods: StagedPricelistPeriod[],
        closeModal: () => void,
        currencyTypeCode?: string
    ): void => {
        setEmployeePricing({ periods, follows: null }, currencyTypeCode);
        closeModal();
    };

    // Following a model stages the model's periods for display only: the save sends the link, never the periods.
    const onFollowPricelist = (
        model: { id: number; name: string },
        periods: StagedPricelistPeriod[],
        currencyTypeCode?: string
    ): void => {
        setEmployeePricing({ periods, follows: model }, currencyTypeCode);
    };

    function setEmployeePricing(
        { periods, follows }: { periods: StagedPricelistPeriod[]; follows: { id: number; name: string } | null },
        currencyTypeCode?: string
    ) {
        setValue('pricingHash', undefined);
        setValue('pricelistRates', []);
        setValue('pricelistPeriods', periods);
        setValue('pricelistFollows', follows);
        setValue('pricelistEdited', true);
        setValue('pricelist', {
            name: getValues('pricelist')?.name ?? '',
            scopeTypeCode: 'CONTRACT',
            currencyTypeCode: currencyTypeCode ?? null,
            pricingKind: PricingKind.EMPLOYEE,
        });
    }

    // Concluded by joins Applied in, and clears the bank account, which belongs to the previous company.
    const prefillAppliedIn = (company: NamedEntityMinDto | null) => {
        const appliedIn = getValues('companies') ?? [];
        if (company && !appliedIn.find((val) => val.id === company.id)) appliedIn.push(company);
        setValue('companies', appliedIn);
        setValue('bankAccount', null);
    };

    const isAmlOrUbo = contractType?.value === ContractType.AML || contractType?.value === ContractType.UBO;
    const isRepresentation = contractType?.value === ContractType.REPRESENTATION;
    const isCooperation = contractType?.value === ContractType.COOPERATION;

    const ibans = bankAccountsGrouped
        .filter((bankAccount) => bankAccount.companyId === getValues('tenantCompany')?.id)
        .flatMap((company) =>
            company.bankAccounts.map((account) => ({
                id: account.id,
                name: account.iban,
                group: company.companyName,
            }))
        );

    return {
        activeCompanyList,
        classifiers,
        contractTemplatesOptions,
        contractType,
        currencyType,
        filteredCustomFields,
        hasAdminFeePermitted,
        ibans,
        initialData,
        isAmlOrUbo,
        isCooperation,
        isLoading: isLoadingCompanies || isLoadingCustomFieldData || isLoadingContractTemplatesOptions,
        isRepresentation,
        isServiceAgreement,
        isTimecardIncrementControlEnabled,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        methods,
        onNewPricelistFormSubmit,
        onNewEmployeePricelistSubmit,
        onFollowPricelist,
        openAddNewContactModal,
        prefillAppliedIn,
        selectedCompanyPartyIds,
        selectedPersonPartyIds,
        timecardIncrementOptions,
        type,
        validUrl,
    };
};

export type ContractFormState = ReturnType<typeof useContractForm>;
